import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  AddServiceFieldsType,
  CreateOfferResponse,
  RequestServiceValidationParams,
  ServiceValidationResponse,
} from "../offerCreate/types";
import { AppThunk } from "../../store";
import { getOfferApi, downloadPdfApi } from "../offerList/api";
import {
  setStepValidated,
  setStepsServicesValidates,
} from "../offerCreate/stepsSlice";
import {
  requestApproveApi,
  offerApproveApi,
  offerRejectApi,
  getOfferHistoryApi,
  getOfferServicesApi,
  validateSelectedServicesApi,
} from "./api";
import {
  OfferResponseServices,
  OfferResponseHistory,
  RequestHistoryParams,
  RequestServicesParams,
  UpdateApproveStatus,
} from "./types";
import { getErrorMessageFromResponse } from "@helpers/get-error-message";
import { isAxiosError } from "axios";

type offerType = {
  offer: CreateOfferResponse | null;
  history: OfferResponseHistory | null;
  services: OfferResponseServices | null;
  loading: boolean;
  error: string | null;
  success: string | null;
  exporting: {
    success: boolean;
    loading: boolean;
    error: string | null;
  };
};

const initialState: offerType = {
  offer: null,
  history: null,
  services: null,
  loading: false,
  error: "",
  success: "",
  exporting: {
    success: false,
    loading: false,
    error: null,
  },
};

const offer = createSlice({
  name: "offer",
  initialState: initialState,
  reducers: {
    getOfferStart(state: offerType) {
      state.loading = true;
      state.error = null;
    },
    getOfferSuccess(
      state: offerType,
      action: PayloadAction<CreateOfferResponse>,
    ) {
      state.offer = action.payload;
      state.loading = false;
      state.error = null;
    },
    getOfferFailure(state: offerType, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
      state.history = null;
      state.offer = null;
      state.services = null;
    },
    cleanOffer(state: offerType) {
      state.offer = null;
    },
    cleanHistory(state: offerType) {
      state.history = null;
    },
    cleanServices(state: offerType) {
      state.services = null;
    },
    updateOffer(state: offerType, action: PayloadAction<UpdateApproveStatus>) {
      if (state.offer) {
        state.offer.status = action.payload.status;
        state.offer.reject_comment = action.payload.reject_comment;
      }
      state.success = action.payload.message;
    },
    hideWarning(state) {
      state.error = null;
      state.success = null;
      state.exporting.success = false;
      state.exporting.error = null;
    },
    getOfferHistorySuccess(
      state: offerType,
      action: PayloadAction<OfferResponseHistory>,
    ) {
      state.history = action.payload;
      state.loading = false;
    },
    getOfferServicesSuccess(
      state: offerType,
      action: PayloadAction<OfferResponseServices>,
    ) {
      state.services = action.payload;
      state.loading = false;
    },
    startExport(state: offerType) {
      state.exporting = {
        loading: true,
        error: null,
        success: false,
      };
    },
    successExport(state: offerType) {
      state.exporting.loading = false;
      state.exporting.success = true;
      state.exporting.error = null;
    },
    getDownloadFailure(state: offerType, action: PayloadAction<string>) {
      state.exporting.error = action.payload;
      state.exporting.loading = false;
    },
    setSelectedServicesValidates(
      state,
      action: PayloadAction<ServiceValidationResponse>,
    ) {
      if (state.services?.items) {
        state.services.items = state.services.items.map((service: any) => {
          if (service.active_price) {
            const updatedActive = action.payload.find(
              (s) => s.active_price.id === service.active_price.id,
            );
            if (updatedActive) {
              return {
                ...service,
                association_id: service.id,
                active_price: {
                  ...service.active_price,
                  is_valid: updatedActive.active_price.is_valid,
                },
                passive_prices: service.passive_prices?.map((price: any) => {
                  const updatedPassive = updatedActive.passive_prices?.find(
                    (p) => p.id === price.id,
                  );
                  return updatedPassive
                    ? { ...price, is_valid: updatedPassive.is_valid }
                    : price;
                }),
              };
            }
          }
          return service;
        });
      }
    },
  },
});

export const {
  getOfferStart,
  getOfferSuccess,
  getOfferFailure,
  cleanOffer,
  cleanHistory,
  cleanServices,
  updateOffer,
  hideWarning,
  getOfferHistorySuccess,
  getDownloadFailure,
  getOfferServicesSuccess,
  startExport,
  successExport,
  setSelectedServicesValidates,
} = offer.actions;

export default offer.reducer;

export const fetchOffer =
  (id: number): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getOfferStart());
      const offer = await getOfferApi(id);
      dispatch(getOfferSuccess(offer));
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 404) {
        dispatch(getOfferFailure(`Offer ${id} not found`));
      } else {
        const error = getErrorMessageFromResponse(err);
        dispatch(getOfferFailure(error));
      }
    }
  };

export const fetchOfferHistory =
  (id: number, params: RequestHistoryParams): AppThunk =>
  async (dispatch) => {
    try {
      const history = await getOfferHistoryApi(id, params);
      dispatch(getOfferHistorySuccess(history));
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err);
      dispatch(getOfferFailure(error));
    }
  };

export const fetchOfferServices =
  (id: number, params: RequestServicesParams): AppThunk =>
  async (dispatch) => {
    try {
      const services = await getOfferServicesApi(id, params);
      dispatch(getOfferServicesSuccess(services));

      if (
        params.flow_of_goods &&
        params.incoterm &&
        params.commodity__id__in &&
        params.port__id__in
      ) {
        dispatch(
          validateSelectedServices(
            {
              id,
              flow_of_goods: params.flow_of_goods,
              incoterm: params.incoterm,
              commodity__id__in: Array.isArray(params.commodity__id__in)
                ? params.commodity__id__in.join(",")
                : params.commodity__id__in,
              port__id__in: Array.isArray(params.port__id__in)
                ? params.port__id__in.join(",")
                : params.port__id__in,
              items: services.items.map((s) => ({
                active_price_id: s.active_price?.id,
                passive_price_ids: (s.passive_prices ?? []).map((p) => p.id),
              })),
            },
            "edit",
          ),
        );
      }
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err);
      dispatch(getOfferFailure(error));
    }
  };

export const validateSelectedServices =
  (
    params: RequestServiceValidationParams,
    type?: string | undefined,
  ): AppThunk =>
  async (dispatch, getState) => {
    try {
      const services = await validateSelectedServicesApi(params);

      if (type) {
        dispatch(setSelectedServicesValidates(services));
        const state = getState();
        const updatedServices = state.offer.services?.items || [];
        dispatch(
          setStepValidated({
            id: "addServices",
            fieldsValues: {
              prices: updatedServices,
            } as AddServiceFieldsType,
          }),
        );
      } else {
        dispatch(setStepsServicesValidates(services));
      }
    } catch (err: any) {
      const error = err instanceof Error ? err.message : String(err);
      dispatch(getOfferFailure(error));
    }
  };

export const downloadPdf =
  (id: number, params: { language: string }): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(startExport());
      const file = await downloadPdfApi(id, params);

      const url = window.URL.createObjectURL(file);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `offer_${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      dispatch(successExport());
    } catch {
      dispatch(getDownloadFailure("Downloading failed. Try again later."));
    }
  };

export const requestApprove =
  (id: number): AppThunk =>
  async (dispatch, getState) => {
    try {
      await requestApproveApi(id);
      dispatch(
        updateOffer({
          status: "PENDING APPROVAL",
          message: "The offer has been successfully send",
          reject_comment: "",
        }),
      );
      const state = getState();
      if (state.offer) {
        dispatch(
          fetchOfferHistory(id, {
            size: 1000,
            page: 1,
            order_by: "created_at",
          }),
        );
      }
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getOfferFailure(error));
    }
  };

export const offerApprove =
  (id: number): AppThunk =>
  async (dispatch, getState) => {
    try {
      await offerApproveApi(id);
      dispatch(
        updateOffer({
          status: "APPROVED",
          message: "The offer has been successfully approved",
          reject_comment: "",
        }),
      );
      const state = getState();
      if (state.offer) {
        dispatch(
          fetchOfferHistory(id, {
            size: 1000,
            page: 1,
            order_by: "created_at",
          }),
        );
      }
    } catch {
      dispatch(getOfferFailure("The offer cannot be approved"));
    }
  };

export const offerReject =
  (id: number, comment: string): AppThunk =>
  async (dispatch, getState) => {
    try {
      await offerRejectApi(id, comment);
      dispatch(
        updateOffer({
          status: "REJECTED",
          reject_comment: comment,
          message: "The offer has been successfully rejected",
        }),
      );
      const state = getState();
      if (state.offer) {
        dispatch(
          fetchOfferHistory(id, {
            size: 1000,
            page: 1,
            order_by: "created_at",
          }),
        );
      }
    } catch {
      dispatch(getOfferFailure("The offer cannot be rejected"));
    }
  };
